// src/components/SinusView.js
import React from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    FormControl,
    InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils';
import { sinusViewOptions } from './SinusViewConstants';

const SinusView = () => {
    const { state, actions } = useFormContext();
    const sinusViewData = state.sinusViewData || { image: null, selectedImpressions: [], otherImpression: '' };

    const handleImageChange = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png, image/jpeg';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) {
                console.log("No file selected.");
                return;
            }

            try {
                const base64 = await fileToBase64(file);
                actions.setSinusViewData({
                    ...sinusViewData,
                    image: {
                        base64,
                        name: file.name,
                        type: file.type
                    }
                });
            } catch (error) {
                console.error("Error during image processing:", error);
                actions.setError("Failed to load image. Please try a different file.");
            }
        };
        input.click();
    };

    const handleImpressionsChange = (event) => {
        const { target: { value } } = event;
        actions.setSinusViewData({
            ...sinusViewData,
            selectedImpressions: typeof value === 'string' ? value.split(',') : value,
        });
    };

    const handleOtherImpressionChange = (event) => {
        actions.setSinusViewData({
            ...sinusViewData,
            otherImpression: event.target.value
        });
    };

    const handleRemoveImage = () => {
        actions.setSinusViewData({ ...sinusViewData, image: null });
    };

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Sinus View
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Sinus View Image
                        </Typography>
                        <Button variant="contained" onClick={handleImageChange} sx={{ mb: 1 }}>
                            {sinusViewData.image ? 'Replace Image' : 'Upload Image'}
                        </Button>
                        
                        {sinusViewData.image && (
                            <Box sx={{ mt: 2, mb: 3, position: 'relative', maxWidth: '400px' }}>
                                <Typography variant="caption" display="block" color="success.main">
                                    {sinusViewData.image.name}
                                </Typography>
                                <img
                                    src={sinusViewData.image.base64}
                                    alt="Sinus View Preview"
                                    style={{ maxWidth: '100%', height: 'auto', marginTop: '10px', border: '1px solid #ddd' }}
                                />
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={handleRemoveImage}
                                    sx={{ position: 'absolute', top: 0, right: 0, backgroundColor: 'white', '&:hover': {backgroundColor: 'rgba(255,255,255,0.8)'} }}
                                    title="Remove Image"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}

                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                            Sinus View Comments
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel id="sinus-view-impressions-label">Select Comments</InputLabel>
                            <Select
                                labelId="sinus-view-impressions-label"
                                multiple
                                value={sinusViewData.selectedImpressions || []}
                                onChange={handleImpressionsChange}
                                renderValue={(selected) => selected.join(', ')}
                            >
                                {sinusViewOptions.map((impression) => (
                                    <MenuItem key={impression} value={impression}>
                                        <Checkbox checked={(sinusViewData.selectedImpressions || []).indexOf(impression) > -1} />
                                        <ListItemText primary={impression} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {(sinusViewData.selectedImpressions || []).includes('Other') && (
                            <TextField
                                label="Other Comments"
                                fullWidth
                                multiline
                                rows={2}
                                margin="normal"
                                value={sinusViewData.otherImpression || ''}
                                onChange={handleOtherImpressionChange}
                            />
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};


export default SinusView;
